import React, {useEffect} from "react";
import {
    ContentDetailInfoShell,
    ContentDetailThreeColLayout,
    DetailFilePanel,
    DetailKvListValue,
    DetailKvReadOnly,
    DetailKvTable,
    DetailPanel,
    KV_LIST_TYPES,
    useDetailEditing,
} from "../../../../../../components/detailLayout";
import {
    progressDiligenceStatus,
    updateDiligenceAsset,
    updateDiligenceHandler,
    updateSingleField,
} from "../../../../../../api/asset/DiligenceService";
import Th_title from "../../../../../../components/Th_title";
import {CacheType, CodeSpecNum, ContentCategory, ContentStatus} from "../../../../../../constant/CodeSpecNum";
import {useCacheRefresh} from "../../../../../../hooks/useCacheRefresh";
import ApprovalComponent from "../../../../../settings/approval/new/ApprovalComponent";
import Stepper from "../../../../../../components/stepper/Stepper";
import UpdateTextArea from "../../../../../../components/input/UpdateTextArea";
import SimmsInput from "../../../../../../components/input/SimmsInput";
import {inputOnChange, inputOnKeyDown} from "../../../../../../components/commonmethod/simmsInputMethod";
import SimmsDatePicker from "../../../../../../components/datepicker/SimmsDatePicker";
import {dateOnChange, dateOnSelected} from "../../../../../../components/commonmethod/simmsDateMethod";
import dayjs from "dayjs";
import ReadOnlyInputBasic from "../../../../../../components/input/ReadOnlyInputBasic";
import SearchSelector from "../../../../../../components/searchSelector/SearchSelector";
import UserSelectionField from "../../../../../../components/selection/UserSelectionField";
import {AssetSearchFilters} from "../../../info/shared/AssetSearchFilters";
import SimmsInputSearch from "../../../../../../components/input/SimmsInputSearch";
import {
    diligenceAssetsOnUpdate,
    diligenceHandlerOnUpdate,
    onUpdateClickListener,
} from "../../../../../../components/commonmethod/simmsDrawerMethod";
import Selector from "../../../../../../components/selector/Selector";
import {useDispatch, useSelector} from "react-redux";
import {changeRefreshContentStatus} from "../../../../../../redux/slice/statusSlice";
import {changeStatusNumericValue} from "../../../../../../redux/slice/contentSlice";
import {commonResponseMessage} from "../../../../../../components/commonmethod/CommonMethod";
import {handleActiveError} from "../../../../../../components/sonner/ToastBasic";
import {apiLoading} from "../../../../../../redux/slice/apiSlice";
import {getApprovalProcessSubmitPayload, clearApprovalProcessState} from "../../../../../settings/approval/shared/approvalProcessStore";
import "../../../../../../components/selection/userSelectionChips.css";

function DiligenceInfo({diligence, setDiligence}) {
    const summaryMeta = [
        diligence.tickets && `번호 ${diligence.tickets}`,
        diligence.code?.name && `종류 ${diligence.code.name}`,
        diligence.contentStatus?.statusKorValue && `상태 ${diligence.contentStatus.statusKorValue}`,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <ContentDetailInfoShell
            title={diligence.title || "실사상세"}
            meta={summaryMeta || undefined}
            canUpdate={diligence.auth?.update ?? false}
            listUrl="/diligence"
            scrollMode="columns"
            topContent={
                <section className="detail-progress-status" aria-label="진행상태">
                    <Stepper
                        contentId={diligence.id}
                        contentCategory={ContentCategory.DILIGENCE}
                        exclude={[ContentStatus.APPROVE]}
                    />
                </section>
            }
        >
            <DiligenceDetailBody diligence={diligence} setDiligence={setDiligence} />
        </ContentDetailInfoShell>
    );
}

function DiligenceDetailBody({diligence, setDiligence}) {
    const platFrom = useSelector((state) => state.auth.platFrom);
    const dispatch = useDispatch();
    const refreshCache = useCacheRefresh();
    const isEditing = useDetailEditing();
    const fieldAuth = isEditing && diligence.auth?.update;

    const diligenceAssetList = (diligence.assets ?? []).map((e) => e.asset).filter(Boolean);

    const approvalProcessId = useSelector((state) => state.content.approvalProcess?.id);
    const beforApproval =
        diligence.contentStatus.statusNumericValue < ContentStatus.APPROVE.statusNumericValue;
    const isCompleted =
        diligence.contentStatus.statusNumericValue >= ContentStatus.COMPLETE.statusNumericValue;

    useEffect(() => {
        if (!beforApproval) {
            return undefined;
        }

        clearApprovalProcessState(dispatch);
        return () => clearApprovalProcessState(dispatch);
    }, [beforApproval, diligence.id, dispatch]);

    const remainYetChecked = diligence.assets.some((e) => e.checked !== true);
    const progressButtonShow =
        diligence.contentStatus.statusNumericValue < 30 ||
        diligence.contentStatus.statusNumericValue > 40;

    const handleAssetsMultiFlat = async (list) => {
        if (!diligence.auth?.update) {
            alert("권한이 없거나 처리할 수없는 단계입니다.");
            return;
        }
        const next = Array.isArray(list) ? list.filter(Boolean) : [];
        const prev = diligenceAssetList;
        const prevIds = new Set(prev.map((a) => a.id));
        const nextIds = new Set(next.map((a) => a.id));

        for (const item of next) {
            if (!prevIds.has(item.id)) {
                const response = await updateDiligenceAsset(diligence.id, item.id, true);
                if (response.status === 200) {
                    setDiligence((p) => ({
                        ...p,
                        assets: [...(p.assets ?? []), {asset: item}],
                    }));
                    commonResponseMessage(response, true, "자산");
                } else if (response.status === 201 || response.status === 202) {
                    handleActiveError({
                        message: response.data.title,
                        description: response.data.reason,
                    });
                }
            }
        }

        for (const item of prev) {
            if (!nextIds.has(item.id)) {
                if (prev.length <= 1) {
                    alert("필수값 이므로 최소한 하나의 요소가 필요합니다.");
                    continue;
                }
                const response = await updateDiligenceAsset(diligence.id, item.id, false);
                if (response.status === 200) {
                    setDiligence((p) => ({
                        ...p,
                        assets: (p.assets ?? []).filter((e) => e.asset?.id !== item.id),
                    }));
                    commonResponseMessage(response, false, "자산");
                } else if (response.status === 201 || response.status === 202) {
                    handleActiveError({
                        message: response.data.title,
                        description: response.data.reason,
                    });
                }
            }
        }
    };

    const handleHandlersMultiFlat = async (list) => {
        const next = Array.isArray(list) ? list.filter(Boolean) : [];
        const prev = diligence?.handler ?? [];
        const prevIds = new Set(prev.map((h) => h.id));
        const nextIds = new Set(next.map((h) => h.id));

        for (const item of next) {
            if (!prevIds.has(item.id)) {
                diligenceHandlerOnUpdate(
                    setDiligence,
                    updateDiligenceHandler,
                    diligence.id,
                    "handler",
                    diligence?.handler,
                    item,
                    true,
                );
            }
        }

        for (const item of prev) {
            if (!nextIds.has(item.id)) {
                if (!diligence.auth?.update) {
                    alert("권한이 없거나 처리할 수없는 단계입니다.");
                    return;
                }
                diligenceHandlerOnUpdate(
                    setDiligence,
                    updateDiligenceHandler,
                    diligence.id,
                    "handler",
                    diligence?.handler,
                    item,
                    true,
                );
            }
        }
    };

    const handleProgress = () => {
        const dto = {
            id: diligence.id,
            ...getApprovalProcessSubmitPayload(),
        };
        if (!progressButtonShow) {
            return;
        }
        if (isCompleted) {
            return;
        }

        const confirmMessage = beforApproval
            ? "진행 하시겠습니까?"
            : remainYetChecked
              ? "실사완료 되지 않은 자산이 있습니다.\n진행하시겠습니까?"
              : "실사를 종료하시겠습니까?";

        if (!window.confirm(confirmMessage)) {
            return;
        }

        progressDiligenceStatus(dto, diligence.contentStatus.statusNumericValue)
            .then((r) => {
                dispatch(apiLoading(true));
                if (r.status === 200) {
                    dispatch(changeStatusNumericValue(0));
                    dispatch(changeRefreshContentStatus());
                    if (!beforApproval) {
                        refreshCache(CacheType.asset);
                    }
                    alert("처리되었습니다.");
                } else if (r.status === 201 || r.status === 202) {
                    handleActiveError({message: r.data.title, description: r.data.reason});
                }
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                dispatch(apiLoading(false));
            });
    };

    const progressLabel = !isCompleted && beforApproval
        ? "진행하기"
        : !isCompleted && !beforApproval
          ? "실사 종료"
          : isCompleted && !beforApproval
            ? "완료됨"
            : "진행하기";

    const basicInfoPanel = (
        <DetailPanel title="기본정보">
            <DetailKvTable>
                <tbody>
                    <tr>
                        <DetailKvReadOnly label="등록일시" value={diligence.createdAt} type="date" />
                        <DetailKvReadOnly label="수정일시" value={diligence.updatedAt} type="date" />
                    </tr>
                    <tr>
                        <DetailKvReadOnly label="등록자" value={diligence.createdBy?.name} />
                        <DetailKvReadOnly label="수정자" value={diligence.updatedBy?.name} />
                    </tr>
                    <tr>
                        <Th_title
                            title="실사명"
                            colspan={1}
                            required
                            className="fieldset-td fieldset-td-input"
                            auth={fieldAuth}
                        >
                            <SimmsInput
                                onChange={(v) => inputOnChange(setDiligence, "title", v, true)}
                                update
                                onKeyDown={(v, e) =>
                                    inputOnKeyDown(
                                        setDiligence,
                                        updateSingleField,
                                        diligence.id,
                                        "title",
                                        v,
                                        e,
                                        true,
                                    )
                                }
                                data={diligence.title}
                            />
                        </Th_title>
                        <Th_title title="실사번호" colspan={1} className="fieldset-td fieldset-td-input">
                            <ReadOnlyInputBasic value={diligence.tickets} />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title
                            title="실사종류"
                            colspan={3}
                            className="fieldset-td fieldset-td-input"
                            auth={fieldAuth}
                        >
                            <Selector
                                onChange={(v) =>
                                    onUpdateClickListener(
                                        setDiligence,
                                        updateSingleField,
                                        "code",
                                        diligence,
                                        v,
                                        false,
                                    )
                                }
                                data={diligence.code}
                                recursive
                                parentKey={CodeSpecNum.DiligenceCode}
                                entity="code"
                            />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title
                            title="요청자"
                            colspan={1}
                            className="fieldset-td fieldset-td-input"
                            auth={fieldAuth}
                        >
                            <Selector
                                onChange={(v) =>
                                    onUpdateClickListener(
                                        setDiligence,
                                        updateSingleField,
                                        "requestedBy",
                                        diligence,
                                        v,
                                        true,
                                    )
                                }
                                data={diligence.requestedBy}
                                entity="userAccount"
                                uniqueKey="requestedBy"
                            >
                                <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                            </Selector>
                        </Th_title>
                        <Th_title
                            title="실사기간"
                            colspan={1}
                            className="fieldset-td fieldset-td-input"
                            required
                            auth={fieldAuth}
                        >
                            <div className="web-flex asset-detail-date-range">
                                <SimmsDatePicker
                                    onChange={(v) => dateOnChange(setDiligence, "reqStartDate", v, false)}
                                    data={diligence.reqStartDate}
                                    update
                                    onUpdate={(v) =>
                                        dateOnSelected(
                                            setDiligence,
                                            updateSingleField,
                                            diligence.id,
                                            "reqStartDate",
                                            v,
                                            false,
                                        )
                                    }
                                    constraint={{
                                        lessThan: diligence.reqEndDate && dayjs(diligence.reqEndDate),
                                    }}
                                    auth={fieldAuth}
                                />
                                <span className="asset-detail-date-range-sep">~</span>
                                <SimmsDatePicker
                                    onChange={(v) => dateOnChange(setDiligence, "reqEndDate", v, false)}
                                    data={diligence.reqEndDate}
                                    update
                                    onUpdate={(v) =>
                                        dateOnSelected(
                                            setDiligence,
                                            updateSingleField,
                                            diligence.id,
                                            "reqEndDate",
                                            v,
                                            false,
                                        )
                                    }
                                    constraint={{
                                        largerThan: diligence.reqStartDate && dayjs(diligence.reqStartDate),
                                    }}
                                    auth={fieldAuth}
                                />
                            </div>
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title
                            title="담당자"
                            colspan={1}
                            className="fieldset-td fieldset-td-input relative align-top"
                            required
                            auth={fieldAuth}
                        >
                            {isEditing ? (
                                <UserSelectionField
                                    layout="inline"
                                    data={diligence.handler}
                                    onRemove={(v) => {
                                        if (!diligence.auth.update) {
                                            alert("권한이 없거나 처리할 수없는 단계입니다.");
                                            return;
                                        }
                                        diligenceHandlerOnUpdate(
                                            setDiligence,
                                            updateDiligenceHandler,
                                            diligence.id,
                                            "handler",
                                            diligence?.handler,
                                            v,
                                            true,
                                        );
                                    }}
                                >
                                    <SearchSelector
                                        auth={fieldAuth}
                                        multiSelect
                                        variant="compact"
                                        entity="userAccount"
                                        uniqueKey="handler"
                                        data={diligence.handler}
                                        placeHolder="사용자명"
                                        onChange={(v) => {
                                            if (v?.__multiFlat) {
                                                handleHandlersMultiFlat(v.__multiFlat);
                                            }
                                        }}
                                    >
                                        <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                                    </SearchSelector>
                                </UserSelectionField>
                            ) : (
                                <DetailKvListValue
                                    items={diligence.handler}
                                    listType={KV_LIST_TYPES.user}
                                />
                            )}
                        </Th_title>
                        <Th_title
                            title="자산목록"
                            colspan={1}
                            className="fieldset-td relative align-top"
                            required
                            auth={fieldAuth}
                        >
                            {isEditing ? (
                                <UserSelectionField
                                    layout="inline"
                                    data={diligenceAssetList}
                                    countUnit="개"
                                    chipEntity="asset-diligence"
                                    onRemove={(v) => {
                                        if (!diligence.auth.update) {
                                            alert("권한이 없거나 처리할 수없는 단계입니다.");
                                            return;
                                        }
                                        if (diligenceAssetList.length <= 1) {
                                            alert("필수값 이므로 최소한 하나의 요소가 필요합니다.");
                                            return;
                                        }
                                        diligenceAssetsOnUpdate(
                                            setDiligence,
                                            updateDiligenceAsset,
                                            diligence.id,
                                            "assets",
                                            diligenceAssetList,
                                            v,
                                            true,
                                        );
                                    }}
                                >
                                    <SearchSelector
                                        auth={fieldAuth}
                                        multiSelect
                                        variant="compact"
                                        entity="asset-diligence"
                                        uniqueKey="assets"
                                        data={diligenceAssetList}
                                        placeHolder="자산명"
                                        onChange={(v) => {
                                            if (v?.__multiFlat) {
                                                handleAssetsMultiFlat(v.__multiFlat);
                                            }
                                        }}
                                    >
                                        <AssetSearchFilters
                                            platFrom={platFrom}
                                            diligenceId={diligence.id}
                                        />
                                    </SearchSelector>
                                </UserSelectionField>
                            ) : (
                                <DetailKvListValue
                                    items={diligenceAssetList}
                                    listType={KV_LIST_TYPES.asset}
                                />
                            )}
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title="실사내용" colspan={3} className="fieldset-td" auth={fieldAuth}>
                            <UpdateTextArea
                                auth={fieldAuth}
                                target="etc"
                                value={diligence.etc}
                                data={{data: diligence, setDiligence}}
                            />
                        </Th_title>
                    </tr>
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    );

    const progressPanel = (
        <DetailPanel title="진행단계">
            <DetailKvTable>
                <tbody>
                    <tr>
                        <Th_title title="진행" colspan={3} className="fieldset-td" auth={diligence.auth?.update}>
                            <button
                                type="button"
                                className="user-selection-chips-view-all diligence-progress-btn"
                                disabled={!progressButtonShow || isCompleted}
                                onClick={handleProgress}
                            >
                                {progressLabel}
                            </button>
                        </Th_title>
                    </tr>
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    );

    const asidePanel = (
        <>
            <ApprovalComponent
                auth={diligence.auth.update}
                contentCategory={ContentCategory.DILIGENCE}
                contentId={beforApproval ? null : diligence.id}
                approvalProcessId={beforApproval ? approvalProcessId : undefined}
                refresh
            />
            <DetailFilePanel
                title="첨부"
                isEditing={Boolean(fieldAuth)}
                tableInfo={{tableName: ContentCategory.DILIGENCE, joinKey: diligence.id}}
            />
        </>
    );

    return (
        <ContentDetailThreeColLayout
            primary={basicInfoPanel}
            secondary={progressPanel}
            aside={asidePanel}
        />
    );
}

export default DiligenceInfo;
